<template>
    <v-container class="d-flex justify-center align-center mt-8">
        <v-card class="mt-16" min-width="800">
            <v-card-title>
                <div>
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn
                                v-bind="attrs"
                                v-on="on"
                                color="primary"
                                icon
                                @click="newCatalogDialog = true"
                                dark>
                                <v-icon>mdi-plus</v-icon>
                            </v-btn>
                        </template>
                        <span>Create Catalog</span>
                    </v-tooltip>
                    Catalogs
                </div>
                <v-spacer/>
                <v-text-field
                    v-model="search"
                    append-icon="mdi-magnify"
                    label="Search"
                    single-line
                    hide-details
                    class="shrink"/>
            </v-card-title>
            <v-card-text>
                <v-data-table
                    :headers="headers"
                    :items="catalogs"
                    :search="search"
                    :loading="loading">
                    <template v-slot:item.Name="{ item }">
                        <v-text-field
                            v-model="item.Name"
                            @change="updateCatalog(item)"
                            dense
                            hide-details/>
                    </template>
                    <template v-slot:item.Preseason="{ item }">
                        <v-switch
                            v-model="item.Preseason"
                            :true-value="1"
                            :false-value="0"
                            @change="updateCatalog(item)"
                            dense
                            hide-details/>
                    </template>
                    <template v-slot:item.Source="{ item }">
                        <v-autocomplete
                            v-model="item.Source"
                            :items="sources"
                            item-text="text"
                            item-value="value"
                            @change="updateCatalog(item)"
                            dense
                            hide-details/>
                    </template>
                    <template v-slot:item.actions="{ item }">
                        <v-menu v-model="item.deleteMenu" offset-y>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn v-bind="attrs" v-on="on" icon>
                                    <v-icon>mdi-dots-vertical</v-icon>
                                </v-btn>
                            </template>
                            <v-list>
                                <v-list-item @click="deleteCatalog(item.Id)">
                                    <v-list-item-title class="red--text">Delete</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>

        <v-dialog width="500" v-model="newCatalogDialog">
            <v-card>
                <v-card-title>Create Catalog</v-card-title>
                <v-card-text>
                    <v-form @submit.prevent="createCatalog">
                        <v-text-field
                            v-model="newCatalog.Id"
                            label="ID"/>
                        <v-text-field
                            v-model="newCatalog.Name"
                            label="Name"/>
                        <v-switch
                            v-model="newCatalog.Preseason"
                            :true-value="1"
                            :false-value="0"
                            label="Preseason"/>
                        <v-autocomplete
                            v-model="newCatalog.Source"
                            :items="sources"
                            item-text="text"
                            item-value="value"
                            label="Source"/>
                        <div class="d-flex justify-space-around mt-4">
                            <v-btn @click="clearNewCatalog()">Cancel</v-btn>
                            <v-btn
                                type="submit"
                                :loading="loading"
                                :disabled="!isFormValid"
                                color="primary">
                                Create
                            </v-btn>
                        </div>
                    </v-form>
                </v-card-text>
            </v-card>
        </v-dialog>

        <v-snackbar
            v-model="snackActive"
            :color="snackColor"
            :timeout="snackTimeout">
            {{ snackText }}
        </v-snackbar>
    </v-container>
</template>

<script>
export default {
    beforeMount() {
        document.title = 'Sandbox - Manage Catalogs'
        this.$store.dispatch('updateShowWaffle', true)
        this.$store.dispatch('updateNavbarContent', [])
        this.$store.dispatch('updateNavbarTitle', 'Manage Catalogs')
        this.getInfo()
    },

    data() {
        return {
            headers: [
                { text: 'ID', value: 'Id' },
                { text: 'Name', value: 'Name' },
                { text: 'Preseason', value: 'Preseason' },
                { text: 'Source', value: 'Source' },
                { text: '', value: 'actions', sortable: false }
            ],
            catalogs: [],
            sources: [
                { text: 'Matador', value: 'Envoy1' },
                { text: 'Matador Global', value: 'Envoy2' }
            ],
            search: '',
            loading: false,
            newCatalog: { Id: '', Name: '', Preseason: 0, Source: '' },
            newCatalogDialog: false,
            snackText: '',
            snackColor: 'success',
            snackTimeout: 3000,
            snackActive: false
        }
    },

    computed: {
        isFormValid() {
            return this.newCatalog.Id && this.newCatalog.Name && this.newCatalog.Source
        }
    },

    methods: {
        snack(text, color, timeout) {
            this.snackText = text
            this.snackColor = color
            this.snackTimeout = timeout
            this.snackActive = true
        },

        async getInfo() {
            try {
                this.loading = true
                const url = new URL('https://1qsj72aerg.execute-api.us-east-2.amazonaws.com/prod/ops/catalog')

                const response = await fetch(url, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' }
                })
                const data = await response.json()
                if (!response.ok) throw new Error(data.message)

                this.catalogs = data.catalogs.map(c => ({ ...c, deleteMenu: false }))
                this.loading = false
            } catch (error) {
                this.loading = false
                this.snack(error.message, 'error', 3000)
            }
        },

        async createCatalog() {
            try {
                this.loading = true
                const url = new URL('https://1qsj72aerg.execute-api.us-east-2.amazonaws.com/prod/ops/catalog')

                const response = await fetch(url, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(this.newCatalog)
                })
                const data = await response.json()
                if (!response.ok) throw new Error(data.message)

                this.catalogs = data.catalogs.map(c => ({ ...c, deleteMenu: false }))
                this.clearNewCatalog()
                this.loading = false
                this.snack('Catalog created', 'success', 3000)
            } catch (error) {
                this.loading = false
                this.snack(error.message, 'error', 3000)
            }
        },

        async updateCatalog(catalog) {
            try {
                this.loading = true
                const url = new URL('https://1qsj72aerg.execute-api.us-east-2.amazonaws.com/prod/ops/catalog')

                const response = await fetch(url, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        Id: catalog.Id,
                        Name: catalog.Name,
                        Preseason: catalog.Preseason,
                        Source: catalog.Source
                    })
                })
                const data = await response.json()
                if (!response.ok) throw new Error(data.message)

                this.loading = false
                this.snack('Catalog updated', 'success', 3000)
            } catch (error) {
                this.loading = false
                this.snack(error.message, 'error', 3000)
            }
        },

        async deleteCatalog(id) {
            try {
                this.loading = true
                const url = new URL('https://1qsj72aerg.execute-api.us-east-2.amazonaws.com/prod/ops/catalog')
                url.searchParams.append('id', id)

                const response = await fetch(url, {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' }
                })
                const data = await response.json()
                if (!response.ok) throw new Error(data.message)

                this.catalogs = data.catalogs.map(c => ({ ...c, deleteMenu: false }))
                this.loading = false
                this.snack('Catalog deleted', 'success', 3000)
            } catch (error) {
                this.loading = false
                this.snack(error.message, 'error', 3000)
            }
        },

        clearNewCatalog() {
            this.newCatalogDialog = false
            this.newCatalog = { Id: '', Name: '', Preseason: 0, Source: '' }
        }
    }
}
</script>
